<script>
  export default {
    name: 'DataTableGolden',

    props: {
      headers: {
        type: Array,
        required: true,
        default: () => [],
      },
      items: {
        type: Array,
        required: true,
        default: () => [],
      },
      isMobile: {
        type: Boolean,
        required: false,
      },
      hasActions: {
        type: Boolean,
        required: false,
      },
    },

    data: () => ({
      games: {
        838: 'Dragonstone',
        856: 'Dragonstone BG',
        859: 'Mission Amazon',
        862: 'Mission Amazon BG',
        864: 'Max Blast',
        875: "Triple Max Blast",
      },
      spacedOutFields: ['buyIn', 'rank', 'date'],
    }),

    computed: {
      headerKeyToText() {
        const obj = {};

        for (const { value, text, icon } of this.headers) {
          obj[value] = {
            text,
            icon,
          };
        }

        return obj;
      },
    },
  };
</script>

<template>
  <div
    :class="{
      'data-table-mobile': isMobile,
    }"
    class="data-table__wrapper"
    @click="$emit('click')"
  >
    <table>
      <thead>
        <tr>
          <th v-for="(item, index) in headers" :key="index" class="max-content pr-3">
            <slot :name="`header.${item.value}`" :item="item">
              <div class="data-table-th">
                <div class="pr-3">
                  <svg v-html="item.icon" width="50px" height="50px" />
                </div>
                <div class="th-title">{{ item.text }}</div>
              </div>
            </slot>
          </th>
          <th v-if="hasActions && items.length" class="action"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!items.length">
          <td colspan="100%" class="card-background-light text-center rounded-borders-2 py-4">
            <span class="w-full disabled--text text--lighten-3 font-size-20 font-weight-superbold">
              The list is empty
            </span>
          </td>
        </tr>
        <tr v-for="(item, index) in items" :key="index" :class="item.active ? '' : 'closed'">
          <template v-for="(value, key) in headerKeyToText">
            <td :key="key" :class="{ 'data-table__mobile-row': isMobile }">
              <template v-if="isMobile">
                <div class="data-table__mobile-row__title pl-3">
                  <svg
                    v-html="headerKeyToText[key].icon"
                    width="75px"
                    height="75px"
                    style="position: relative; left: -20px"
                  />
                  <span>
                    {{ headerKeyToText[key].text }}
                  </span>
                </div>
                <slot :name="`item.${key}`" :item="item">
                  <div class="data-table__mobile-row__cell pr-3">
                    {{ item[key] }}
                  </div>
                </slot>
              </template>
              <slot v-else :name="`item.${key}`" :item="item">
                <div
                  style="margin-left: 50px; width: max-content"
                  :class="{
                    'text-center': key === 'prizeWin',
                  }"
                  class="px-3"
                >
                  {{ item[key] }}
                </div>
              </slot>
            </td>
          </template>
          <td v-if="hasActions && items.length" class="action">
            <slot name="actions" :item="item" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
  .data-table__wrapper {
    max-height: calc(100vh - 400px);
    min-height: 400px;
    overflow: auto;

    table {
      width: 100%;
      border-collapse: separate !important;
      border-spacing: 0 25px !important;
      padding-right: 8px;
      height: 1px;

      thead {
        tr {
          th {
            .data-table-th {
              display: flex;
              align-items: center;
              min-width: max-content;
              width: 100%;

              .th-title {
                width: 100%;
                border-top: thin solid var(--v-gold-base);
                border-bottom: thin solid var(--v-gold-base);
                text-align: left;
                text-transform: uppercase;
                color: var(--v-gold-base);
                font-size: 16px;
                font-weight: 600;
                padding-right: 12px;
              }
            }

            &:nth-last-child(1 of :not(.action)) {
              .th-title {
                border-radius: 0 6px 6px 0;
                border-right: thin solid var(--v-gold-base);
              }
            }

            &.action {
              max-width: 260px;
              width: 150px;
            }
          }
        }
      }

      tbody {
        tr {
          position: relative;
          height: 36px;

          td {
            position: relative;
            color: white;
            min-width: max-content !important;
            font-weight: 600;

            &:first-child {
              //min-width: max-content;
              border-radius: 10px 0 0 10px;
            }

            &:nth-last-child(1 of :not(.action)) {
              border-radius: 0 10px 10px 0;
            }

            &:not(.action) {
              background: var(--v-card-background-base);
            }
          }

          &.closed {
            .v-btn {
              pointer-events: none;
              background: var(--v-disabled-darker-base) !important;
              color: var(--v-disabled-base) !important;
            }
          }

          &.action {
            .actions-container {
              max-width: 260px;
              width: 260px;
            }
          }
        }
      }

      &:not(.data-table-mobile) {
        &:not(.closed):nth-child(even) {
          td:not(.action) {
            background: var(--v-main-light-base);
          }
        }
      }
    }

    &.data-table-mobile {
      table {
        border-collapse: initial !important;
        border-spacing: initial !important;

        thead {
          display: none !important;
        }

        tbody {
          display: flex;
          flex-direction: column;

          tr {
            height: inherit !important;
            margin-bottom: 24px;

            td {
              align-items: center;
              display: flex;
              justify-content: space-between;
              margin: 16px 60px;
              border-radius: 20px;

              &:nth-child(odd) {
                height: 36px;
                background: var(--v-card-background-base);
                max-height: 36px;

                span {
                  margin-left: 12px;
                }

                svg::v-deep {
                  path,
                  polygon,
                  rect,
                  circle {
                    fill: #232323 !important;
                  }
                }
              }

              &:nth-child(even) {
                margin: 8px 12px;
                max-height: 40px;
                background: var(--v-main-light-base);

                .data-table__mobile-row__title {
                  svg::v-deep {
                    path,
                    polygon,
                    rect,
                    circle {
                      fill: #4e3565 !important;
                    }
                  }

                  span {
                    margin-left: 62px;
                    margin-right: 12px;
                  }
                }

                & > div:nth-child(2),
                .data-table__mobile-row__cell {
                  margin-right: 46px;
                  max-width: 60%;
                }
              }

              .data-table__mobile-row__title {
                min-width: 40%;
                align-items: center;
                display: flex;
                text-transform: uppercase;
                font-weight: 700;
                padding-right: 8px;
                max-height: inherit;
                overflow: hidden;
                border-radius: 20px 0 0 20px !important;
              }
            }

            &:after {
              content: '';
              background: none;
              border-radius: 0;
              position: absolute;
              bottom: -12px;
              left: 60px;
              width: calc(100% - 120px);
              border-bottom: thin solid white;
            }
          }
        }
      }
    }

    .v-btn.disabled {
      pointer-events: none;
      background: var(--v-disabled-darker-base) !important;
      color: var(--v-disabled-base) !important;
    }

    /* scrollbar */
    &::-webkit-scrollbar {
      background: #291d30 !important;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: #291d30 !important;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: white !important;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #383838 !important;
    }
  }
  @media screen and (max-width: 768px) {
    .action {
      background: transparent !important;
    }
  }
  @media screen and (max-width: 600px) {
    .data-table__wrapper {
      max-height: calc(100vh - 385px);

      table {
        padding-right: 0 !important;

        tbody {
          tr {
            td {
              &:nth-child(odd) {
                margin: 16px 32px !important;

                .data-table__mobile-row__cell {
                  margin-right: 4px !important;
                }
              }

              &:nth-child(even) {
                .data-table__mobile-row__title {
                  span {
                    margin-left: 32px !important;
                  }
                }

                .data-table__mobile-row__cell {
                  margin-right: 24px !important;
                }
              }
            }
          }
        }
      }
    }
  }
</style>
